import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Share2,
  Copy,
  Check,
  RotateCw,
  ShieldOff,
  Eye,
  Activity,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Tiny relative-time helper. Returns e.g. "12s ago", "3m ago", "2h ago".
function relativeFromNow(iso) {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(diffMs) || diffMs < 0) return null;
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/**
 * Share an AI workspace as a read-only public link.
 *
 * UX flow:
 *  - Not yet shared → shows "Generate share link" CTA.
 *  - Shared → shows the URL with copy-to-clipboard, plus rotate / revoke.
 */
export default function ShareDialog({ incident, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  // Tick once a second so "12s ago" stays accurate while dialog is open.
  const [, setTick] = useState(0);

  const enabled = Boolean(incident?.share_enabled && incident?.share_token);
  const shareUrl = enabled
    ? `${window.location.origin}/share/${incident.share_token}`
    : "";

  // While the dialog is open and a share link is active, poll the incident
  // every 6s so the watcher counter / last-viewed badge stay fresh.
  useEffect(() => {
    if (!open || !enabled || !incident?.id) return undefined;
    const refresh = async () => {
      try {
        const fresh = await api.getIncident(incident.id);
        onUpdate?.(fresh);
      } catch (_) {
        /* ignore — owner can retry */
      }
    };
    refresh();
    const poll = setInterval(refresh, 6000);
    const tick = setInterval(() => setTick((t) => t + 1), 1000);
    return () => {
      clearInterval(poll);
      clearInterval(tick);
    };
  }, [open, enabled, incident?.id, onUpdate]);

  const enable = async () => {
    setBusy(true);
    try {
      const res = await api.shareEnable(incident.id);
      onUpdate?.({
        ...incident,
        share_token: res.share_token,
        share_enabled: res.share_enabled,
        share_created_at: res.share_created_at,
      });
      toast.success("Read-only share link is live");
    } catch (e) {
      toast.error("Could not generate share link");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    try {
      await api.shareRevoke(incident.id);
      onUpdate?.({
        ...incident,
        share_token: null,
        share_enabled: false,
        share_created_at: null,
      });
      toast.success("Share link revoked");
    } catch (e) {
      toast.error("Could not revoke link");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Copy failed — select & copy manually");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid="share-warroom-btn"
          className={`rounded-sm ${
            enabled
              ? "border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              : "border-white/15 text-zinc-300 hover:bg-white/5"
          }`}
        >
          <Share2 className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} />
          {enabled ? "Share link active" : "Share workspace"}
        </Button>
      </DialogTrigger>

      <DialogContent
        data-testid="share-dialog"
        className="cre-surface border-white/10 max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Eye className="h-4 w-4 text-emerald-300" strokeWidth={1.75} />
            Share AI workspace — read-only
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Drop this link into Slack so stakeholders can watch agents reason
            in real time. The page is fully read-only — no approval, archive,
            or feedback controls. Revoke anytime.
          </DialogDescription>
        </DialogHeader>

        {!enabled ? (
          <div className="py-3 text-sm text-zinc-400">
            <div className="border border-white/10 rounded-sm p-4 bg-white/[0.02] space-y-2 text-xs font-mono">
              <div>· One-incident scope (no dashboard access)</div>
              <div>· Opaque 24-byte token, no auth backdoor</div>
              <div>· Live polling — observers see status as it updates</div>
              <div>· Revoke instantly to invalidate every shared link</div>
            </div>
          </div>
        ) : (
          <div className="py-3 space-y-3">
            <div className="cre-overline">Public read-only URL</div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={shareUrl}
                data-testid="share-link-input"
                className="flex-1 rounded-sm border border-white/15 bg-black/40 px-3 py-2 text-xs font-mono text-emerald-300 focus:outline-none focus:border-emerald-500/40"
                onFocus={(e) => e.target.select()}
              />
              <Button
                onClick={copy}
                size="sm"
                data-testid="share-copy-btn"
                className="rounded-sm bg-emerald-500 hover:bg-emerald-400 text-black font-semibold shrink-0"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 mr-1.5" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                  </>
                )}
              </Button>
            </div>
            {incident.share_created_at ? (
              <div className="text-[11px] font-mono text-zinc-500">
                created · {new Date(incident.share_created_at).toLocaleString()}
              </div>
            ) : null}

            {(() => {
              const count = incident.share_view_count || 0;
              const lastIso = incident.share_last_viewed_at;
              const lastMs = lastIso ? Date.now() - new Date(lastIso).getTime() : null;
              const liveNow = lastMs !== null && lastMs < 30000;
              const rel = relativeFromNow(lastIso);
              return (
                <div
                  data-testid="share-watcher-stats"
                  className="flex items-center gap-3 border border-white/10 rounded-sm p-3 bg-white/[0.02]"
                >
                  <div className="flex items-center gap-2">
                    {liveNow ? (
                      <span
                        data-testid="share-live-now"
                        className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-emerald-300"
                      >
                        <Activity className="h-3 w-3 animate-pulse" />
                        live now
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                        <Eye className="h-3 w-3" />
                        idle
                      </span>
                    )}
                  </div>
                  <div className="flex-1 text-right">
                    <div
                      data-testid="share-view-count"
                      className="font-heading text-2xl font-black tracking-tighter text-zinc-100"
                    >
                      {count}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                      total views
                    </div>
                  </div>
                  <div className="flex-1 text-right">
                    <div
                      data-testid="share-last-viewed"
                      className="text-sm text-zinc-200"
                    >
                      {rel || "never"}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                      last viewed
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {enabled ? (
            <>
              <Button
                onClick={enable}
                disabled={busy}
                variant="outline"
                size="sm"
                data-testid="share-rotate-btn"
                className="rounded-sm border-white/15"
              >
                <RotateCw className="h-3.5 w-3.5 mr-2" /> Rotate token
              </Button>
              <Button
                onClick={revoke}
                disabled={busy}
                size="sm"
                data-testid="share-revoke-btn"
                className="rounded-sm bg-rose-500 hover:bg-rose-400 text-white font-semibold"
              >
                <ShieldOff className="h-3.5 w-3.5 mr-2" /> Revoke link
              </Button>
            </>
          ) : (
            <Button
              onClick={enable}
              disabled={busy}
              size="sm"
              data-testid="share-generate-btn"
              className="rounded-sm bg-blue-500 hover:bg-blue-400 text-white font-semibold"
            >
              <Share2 className="h-3.5 w-3.5 mr-2" /> Generate share link
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
